import { useEffect, useState } from "react";
import { layoutGroupBoundsSchema, type LayoutGroup } from "@proof/core";
import { Button, Control } from "./components";
const properties = [
  ["minWidth", "Minimum group width (pt)"],
  ["maxWidth", "Maximum group width (pt)"],
  ["minHeight", "Minimum group height (pt)"],
  ["maxHeight", "Maximum group height (pt)"],
] as const;
type Bounds = Pick<LayoutGroup, (typeof properties)[number][0]>;
export function GroupBounds({
  group,
  onChange,
}: {
  group: LayoutGroup;
  onChange: (bounds: Bounds) => void;
}) {
  const initial = () =>
    Object.fromEntries(
      properties.map(([key]) => [key, String(group[key] ?? "")]),
    ) as Record<keyof Bounds, string>;
  const [draft, setDraft] = useState(initial),
    [error, setError] = useState("");
  const encoded = JSON.stringify(properties.map(([key]) => group[key]));
  useEffect(() => {
    setDraft(initial());
    setError("");
  }, [encoded]);
  return (
    <details className="group-bounds">
      <summary>Group size limits</summary>
      <p className="inspector-note">
        Bound the group's layout box before alignment, anchoring and parent
        placement. Blank limits are unrestricted. Child sizes stay authored;
        Clip contents to group crops overflowing artwork.
      </p>
      <div className="control-grid">
        {properties.map(([key, label]) => (
          <Control key={key} label={label}>
            <input
              type="number"
              min={key.startsWith("min") ? 0 : 0.001}
              max={14400}
              step={0.1}
              value={draft[key]}
              onChange={(event) =>
                setDraft({ ...draft, [key]: event.target.value })
              }
            />
          </Control>
        ))}
      </div>
      {error && <p role="alert">{error}</p>}
      <div className="button-row">
        <Button
          onClick={() => {
            const parsed = layoutGroupBoundsSchema.safeParse(
              Object.fromEntries(
                properties.map(([key]) => [
                  key,
                  draft[key].trim() === "" ? undefined : Number(draft[key]),
                ]),
              ),
            );
            if (!parsed.success) {
              setError(
                parsed.error.issues.map((issue) => issue.message).join(". "),
              );
              return;
            }
            onChange(parsed.data);
            setError("");
          }}
        >
          Apply group limits
        </Button>
        <Button
          onClick={() => {
            setDraft(initial());
            setError("");
          }}
        >
          Reset group limit draft
        </Button>
        {properties.some(([key]) => group[key] !== undefined) && (
          <Button
            onClick={() =>
              onChange({
                minWidth: undefined,
                maxWidth: undefined,
                minHeight: undefined,
                maxHeight: undefined,
              })
            }
          >
            Remove group limits
          </Button>
        )}
      </div>
    </details>
  );
}
